import type {
  FileResponse,
  OshaEmploymentInformationRequest,
  OshaEmploymentInformationResponse,
  OshaEstablishmentInformationResponse,
  OshaSummaryResponse,
} from 'src/api/ProactApiClient';
import {oshaApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

export interface OshaProjectYearArgs {
  projectId: number;
  year: number;
}

export interface OshaPdfArgs extends OshaProjectYearArgs {
  timeZone: string;
}

const projectYearTagId = ({projectId, year}: OshaProjectYearArgs) =>
  `${projectId}:${year}`;

export const oshaApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getOshaSummary: builder.query<OshaSummaryResponse, OshaProjectYearArgs>({
      queryFn: clientQuery(({projectId, year}: OshaProjectYearArgs) =>
        oshaApiClient.getSummary(projectId, year)
      ),
      providesTags: (_result, _error, args) => [
        {type: 'OshaSummary', id: projectYearTagId(args)},
      ],
    }),
    getOshaEmploymentInformation: builder.query<
      OshaEmploymentInformationResponse,
      OshaProjectYearArgs
    >({
      queryFn: clientQuery(({projectId, year}: OshaProjectYearArgs) =>
        oshaApiClient.getEmploymentInformation(projectId, year)
      ),
      providesTags: (_result, _error, args) => [
        {type: 'OshaEmploymentInformation', id: projectYearTagId(args)},
      ],
    }),
    updateOshaEmploymentInformation: builder.mutation<
      OshaEmploymentInformationResponse,
      OshaEmploymentInformationRequest
    >({
      queryFn: clientQuery(params =>
        oshaApiClient.updateEmploymentInformation(params)
      ),
      invalidatesTags: (_result, _error, params) => [
        {type: 'OshaEmploymentInformation', id: projectYearTagId(params)},
        {type: 'OshaSummary', id: projectYearTagId(params)},
      ],
    }),
    getOshaEstablishmentInformation: builder.query<
      OshaEstablishmentInformationResponse,
      number
    >({
      queryFn: clientQuery((projectId: number) =>
        oshaApiClient.getEstablishmentInformation(projectId)
      ),
      providesTags: (_result, _error, projectId) => [
        {type: 'OshaEstablishment', id: projectId},
      ],
    }),
    getOshaPdf: builder.mutation<FileResponse | null, OshaPdfArgs>({
      queryFn: clientQuery(({projectId, year, timeZone}: OshaPdfArgs) =>
        oshaApiClient.getPdf(projectId, year, timeZone)
      ),
    }),
  }),
});

export const {
  useGetOshaSummaryQuery,
  useGetOshaEmploymentInformationQuery,
  useUpdateOshaEmploymentInformationMutation,
  useGetOshaEstablishmentInformationQuery,
  useGetOshaPdfMutation,
} = oshaApi;
